import { Box, BoxProps, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import RouteLink from '@/components/RouteLink';
import { RegistryCrd, RegistryPackage } from '@/graphql/upbound-graphql';
import { isRegistryCRD } from '@/utils/guards/models';
import useParams from '@/utils/hooks/useParams';

import PackageTabContent from './PackageTabContent';

const [firstColumnWidth, secondColumnWidth, thirdColumnWidth] = ['415px', '415px', '160px'];

const headers = [
  {
    key: 'crds-kind-header',
    children: 'Kind',
    width: firstColumnWidth,
  },
  {
    key: 'crds-group-header',
    children: 'Group',
    width: secondColumnWidth,
  },
  {
    key: 'crds-version-header',
    children: 'Version',
    width: thirdColumnWidth,
  },
];

const TabContentCrds: React.FC<
  BoxProps & {
    data: RegistryPackage;
  }
> = ({ data, ...props }) => {
  const router = useRouter();
  const params = useParams(router);
  const query = decodeURIComponent(params.get('query') || '');

  const prepend = `/${data.packageType.toLowerCase()}s/${data.account}/${data.name}/${data.version}`;

  const crds = data.resources.reduce<Array<RegistryCrd>>((result, resource) => {
    if (!isRegistryCRD(resource)) {
      return result;
    }

    if (!query) {
      return [...result, resource];
    }

    const sanitizedValue = query.toLowerCase().trim();

    if (
      !resource.group.toLowerCase().includes(sanitizedValue) &&
      !resource.kind.toLowerCase().includes(sanitizedValue) &&
      !resource.storageVersion.toLowerCase().includes(sanitizedValue)
    ) {
      return result;
    }

    return [...result, resource];
  }, []);

  const rows = useMemo(() => {
    if (crds.length < 1) {
      return (
        <Box m="30px 0">
          <Text textAlign="center" fontStyle="oblique">
            No CRD&apos;s found.
          </Text>
        </Box>
      );
    }

    return crds.map(row => (
      <Box
        key={`crd-row-${row.group}/${row.kind}/${row.storageVersion}`}
        p={{ base: '30px 0', xl: '30px 0 24px 0' }}
        borderBottom="1px solid"
        borderBottomColor="white.4"
        width="100%"
      >
        <Flex display={{ base: 'flex', xl: 'none' }} flexDirection="column" alignItems="flex-start">
          <RouteLink
            to="resourceDetails"
            prepend={prepend}
            params={{
              resourceGroup: row.group,
              resourceKind: row.kind,
              resourceVersion: row.storageVersion,
            }}
            fontSize="14px"
            fontWeight={900}
            textDecoration="underline"
            _hover={{ textDecoration: 'none' }}
            color="grey.0"
            mb="10px"
            noOfLines={1}
          >
            {row.kind}
          </RouteLink>
          <Text as="span" fontSize="14px" color="grey.0" mb="10px" noOfLines={1}>
            {row.group}
          </Text>
          <Text as="span" fontSize="14px" color="grey.0">
            {row.storageVersion}
          </Text>
        </Flex>
        <Flex display={{ base: 'none', xl: 'flex' }}>
          <Box width={firstColumnWidth} mr="auto" p="0 15px" noOfLines={1}>
            <RouteLink
              to="resourceDetails"
              prepend={prepend}
              params={{
                resourceGroup: row.group,
                resourceKind: row.kind,
                resourceVersion: row.storageVersion,
              }}
              lineHeight="20px"
              fontSize="14px"
              fontWeight={900}
              textDecoration="underline"
              _hover={{ textDecoration: 'none' }}
              color="grey.0"
              verticalAlign="text-top"
            >
              {row.kind}
            </RouteLink>
          </Box>
          <Box width={secondColumnWidth} mr="auto" p="0 15px">
            <Text as="span" fontSize="14px" lineHeight="20px" color="grey.0" noOfLines={1}>
              {row.group}
            </Text>
          </Box>
          <Box width={thirdColumnWidth} p="0 15px">
            <Text as="span" fontSize="14px" lineHeight="18px" color="grey.0" verticalAlign="text-top">
              {row.storageVersion}
            </Text>
          </Box>
        </Flex>
      </Box>
    ));
  }, [prepend, crds]);

  return (
    <PackageTabContent
      {...props}
      mt="35px"
      description="Custom Resource Definitions (CRDs) define new API types in a Crossplane cluster."
      placeholder={`Search for CRDs (e.g. ${data.resources
        .slice(0, 2)
        .map(r => r.kind)
        .join(', ')})`}
      headers={headers}
      rows={rows}
      prepend={`${prepend}/crds`}
    />
  );
};

export default TabContentCrds;
